import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import CosmicIcon from '../common/CosmicIcon';
import './CosmicCheckoutModal.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getTierDisplayName = (tier) => {
  switch (tier) {
    case 'ENHANCED': return 'Stellar Map';
    case 'PREMIUM': return 'Complete Universe';
    default: return 'Unknown Tier';
  }
};

const getTierDescription = (tier) => {
  switch (tier) {
    case 'ENHANCED': return 'High-precision constellation with interactive exploration';
    case 'PREMIUM': return 'Complete analysis with professional insights and PDF report';
    default: return '';
  }
};

const getTierIcon = (tier) => {
  switch (tier) {
    case 'ENHANCED': return 'tier_enhanced';
    case 'PREMIUM': return 'tier_premium';
    default: return 'tier_free';
  }
};

const fallbackPriceFor = (targetTier, currentTier) => {
  const t = String(targetTier || '').toUpperCase();
  const c = String(currentTier || 'FREE').toUpperCase();
  if (t === 'ENHANCED') return 24.99;
  if (t === 'PREMIUM') return c === 'ENHANCED' ? 14.99 : 39.99;
  return 0;
};

// Cosmic-dark theming for the Stripe Payment Element. The Element renders in an
// iframe, so it can only be styled through Stripe's appearance API (not our CSS).
// Strategy: lean on Stripe's professionally-tuned `night` theme for the field
// polish (elevation, hover/focus states, wallet + Link buttons), and apply only
// the cosmic identity on top: gold accent, navy surface, Inter, our radius.
// A couple of light rules carry the gold accent onto the focus ring + selected
// tab; everything else stays Stripe-native on purpose.
const cosmicAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#FFD700',
    colorBackground: '#131c34',
    colorText: '#ffffff',
    colorDanger: '#FCA5A5',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSizeBase: '16px',
    borderRadius: '10px',
  },
  rules: {
    '.Tab': {
      backgroundColor: '#0f1830',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: 'none',
    },
    '.Tab:hover': {
      backgroundColor: '#16203c',
    },
    // Keep the selected tab DARK (a faint gold tint over navy) with a gold border
    // and white label, so it never renders as unreadable white-on-yellow.
    '.Tab--selected': {
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      border: '1px solid #FFD700',
      boxShadow: 'none',
    },
    '.TabLabel': { color: 'rgba(255, 255, 255, 0.85)' },
    '.TabLabel--selected': { color: '#ffffff' },
    '.TabIcon': { fill: 'rgba(255, 255, 255, 0.7)' },
    '.TabIcon--selected': { fill: '#FFD700' },
    '.Label': { color: 'rgba(255, 255, 255, 0.85)', fontWeight: '500' },
    '.Input': {
      backgroundColor: '#0f1830',
      border: '1px solid rgba(255, 255, 255, 0.15)',
    },
    '.Input::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
    '.Input:focus': {
      border: '1px solid #FFD700',
      boxShadow: '0 0 0 2px rgba(255, 215, 0, 0.25)',
    },
  },
};

const paymentElementOptions = {
  layout: 'tabs',
  // We collect name + email ourselves (prefilled from the signed-in profile and
  // reused to prefill registration), so suppress them in the Element.
  fields: { billingDetails: { name: 'never', email: 'never' } },
};

/**
 * Cosmic Checkout Modal. The outer component creates the PaymentIntent up front
 * (the server is the single source of truth for the amount and already enables
 * automatic_payment_methods, so cards, Link, Apple Pay, and Google Pay all show
 * in one cosmic-dark Payment Element), then mounts <Elements> with the resulting
 * clientSecret.
 */
const CosmicCheckoutModal = ({
  isOpen,
  onClose,
  targetTier,
  currentTier = 'FREE',
  userInfo = null,
  onSuccess,
  onError,
}) => {
  if (!isOpen) return null;

  return (
    <div className="cosmic-checkout-overlay" onClick={onClose}>
      <div className="cosmic-checkout-container" onClick={e => e.stopPropagation()}>
        <CheckoutLoader
          targetTier={targetTier}
          currentTier={currentTier}
          userInfo={userInfo}
          onClose={onClose}
          onSuccess={onSuccess}
          onError={onError}
        />
      </div>
    </div>
  );
};

/**
 * Creates the PaymentIntent and renders the modal chrome. The Payment Element
 * needs the clientSecret at <Elements> mount, so we fetch it here, before the
 * Stripe provider, and show the header + order summary the whole time so the
 * modal never flashes blank.
 */
const CheckoutLoader = ({ targetTier, currentTier, userInfo, onClose, onSuccess, onError }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [serverAmount, setServerAmount] = useState(null);
  const [initError, setInitError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Server-authoritative pricing: the callable derives the amount from the
        // requested tier. The client never sends a price.
        const callable = httpsCallable(functions, 'createPaymentIntent');
        const { data } = await callable({ targetTier, currentTier });
        if (!active) return;
        if (data && data.clientSecret) {
          setClientSecret(data.clientSecret);
          if (typeof data.amount === 'number') setServerAmount(data.amount);
        } else {
          setInitError('Failed to initialize payment. Please try again.');
        }
      } catch (err) {
        console.error('Payment intent creation error:', err);
        if (active) setInitError('Payment initialization failed. Please check your connection.');
      }
    })();
    return () => { active = false; };
  }, [targetTier, currentTier]);

  const displayPrice = serverAmount != null ? serverAmount / 100 : fallbackPriceFor(targetTier, currentTier);

  return (
    <>
      {/* Header */}
      <div className="cosmic-checkout-header">
        <h2 className="cosmic-checkout-title">Complete Your Cosmic Journey</h2>
        <button className="cosmic-close-btn" onClick={onClose}>
          <span className="close-icon"><CosmicIcon name="ui_close" size={22} decorative /></span>
        </button>
      </div>

      {/* Order Summary */}
      <div className="cosmic-order-summary">
        <div className="cosmic-order-header">
          <h3 className="order-title">Your Selection</h3>
        </div>

        <div className="cosmic-order-item">
          <div className="order-item-icon">
            <CosmicIcon name={getTierIcon(targetTier)} size={28} decorative />
          </div>
          <div className="order-item-details">
            <div className="order-item-name">{getTierDisplayName(targetTier)}</div>
            <div className="order-item-description">{getTierDescription(targetTier)}</div>
          </div>
        </div>

        <div className="cosmic-order-total">
          <div className="order-total-row">
            <span className="total-label">Total</span>
            <span className="total-amount">${displayPrice}</span>
          </div>
          <div className="order-total-note">One-time payment • Instant access</div>
        </div>
      </div>

      {initError ? (
        <div className="cosmic-checkout-loading">
          <div className="cosmic-payment-error">
            <span className="error-icon"><CosmicIcon name="ui_alert" size={16} decorative /></span>
            <span>{initError}</span>
          </div>
        </div>
      ) : !clientSecret ? (
        <div className="cosmic-checkout-loading">
          <div className="cosmic-loading-constellation">
            <div className="loading-star loading-star--1"></div>
            <div className="loading-star loading-star--2"></div>
            <div className="loading-star loading-star--3"></div>
          </div>
          <p className="cosmic-checkout-loading-text">Preparing a secure checkout...</p>
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: cosmicAppearance }}>
          <CheckoutForm
            targetTier={targetTier}
            userInfo={userInfo}
            displayPrice={displayPrice}
            onSuccess={onSuccess}
            onError={onError}
          />
        </Elements>
      )}
    </>
  );
};

/**
 * Payment form (inside the Stripe Elements provider). Renders the Payment
 * Element (cards + wallets + Link) and confirms against the already-created
 * PaymentIntent. The stripeWebhook grants the tier claim server-side on success.
 */
const CheckoutForm = ({ targetTier, userInfo, displayPrice, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { refreshClaims } = useAuth();

  const [paymentStep, setPaymentStep] = useState('details'); // 'details' | 'processing' | 'success'
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [billingInfo, setBillingInfo] = useState({
    name: userInfo?.name || '',
    email: userInfo?.email || '',
  });

  const handleBillingChange = (field, value) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not ready. Please try again.');
      return;
    }
    if (!billingInfo.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!billingInfo.email.trim() || !EMAIL_RE.test(billingInfo.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setProcessing(true);
    setError('');
    setPaymentStep('processing');

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: { name: billingInfo.name, email: billingInfo.email },
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setPaymentStep('details');
        setProcessing(false);
        if (onError) onError(stripeError);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        setPaymentStep('success');

        // Persist the billing name/email so the post-payment registration form
        // can prefill them (we ask once).
        localStorage.setItem('cosmic-payment-success', JSON.stringify({
          paymentIntent: paymentIntent.id,
          tier: targetTier,
          amount: displayPrice,
          name: billingInfo.name,
          email: billingInfo.email,
          timestamp: new Date().toISOString(),
        }));

        setTimeout(async () => {
          // The Stripe webhook grants the tier custom claim server-side; refresh
          // the ID token so the client picks up the new entitlement.
          try {
            await refreshClaims();
          } catch (claimErr) {
            console.warn('Could not refresh tier claim:', claimErr);
          }
          if (onSuccess) onSuccess(paymentIntent);
        }, 2000);
      } else {
        setError('Payment was not completed. Please try again.');
        setPaymentStep('details');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment confirmation error:', err);
      setError('Payment processing failed. Please try again.');
      setPaymentStep('details');
      setProcessing(false);
      if (onError) onError(err);
    }
  };

  return (
    <>
      {(paymentStep === 'details' || paymentStep === 'processing') && (
        <div className="cosmic-payment-section">

          {/* Trust line (Stripe handles PCI/encryption; keep this minimal) */}
          <div className="cosmic-payment-trust">
            <span className="trust-stripe-lockup">
              <span className="trust-stripe-icon"><CosmicIcon name="ui_locked" size={14} decorative /></span>
              <span className="trust-stripe-text">Secured by <span className="stripe-wordmark">Stripe</span> · 256-bit SSL</span>
            </span>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="cosmic-payment-form">

            {/* Contact (prefilled, reused for registration) */}
            <div className="cosmic-billing-section">
              <h4 className="cosmic-section-title">Your Details</h4>

              <div className="cosmic-form-row">
                <div className="cosmic-form-group">
                  <label className="cosmic-label">Full Name*</label>
                  <input
                    type="text"
                    value={billingInfo.name}
                    onChange={(e) => handleBillingChange('name', e.target.value)}
                    className="cosmic-input"
                    placeholder="Enter your full name"
                    required
                    disabled={processing}
                  />
                </div>

                <div className="cosmic-form-group">
                  <label className="cosmic-label">Email Address*</label>
                  <input
                    type="email"
                    value={billingInfo.email}
                    onChange={(e) => handleBillingChange('email', e.target.value)}
                    className="cosmic-input"
                    placeholder="your.email@example.com"
                    required
                    disabled={processing}
                  />
                </div>
              </div>
            </div>

            {/* Payment Information (Payment Element: cards, Link, Apple/Google Pay) */}
            <div className="cosmic-card-section">
              <h4 className="cosmic-section-title">Payment Method</h4>

              <div className="cosmic-payment-element">
                <PaymentElement
                  options={paymentElementOptions}
                  onChange={(event) => setPaymentReady(event.complete)}
                />
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="cosmic-payment-error">
                <span className="error-icon"><CosmicIcon name="ui_alert" size={16} decorative /></span>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className={`cosmic-cta cosmic-cta--full cosmic-payment-btn ${processing ? 'loading' : ''}`}
              disabled={!stripe || processing || !paymentReady}
            >
              {processing ? (
                <>
                  <div className="cosmic-loading-spinner cosmic-loading-spinner--sm"></div>
                  <span>Processing Payment...</span>
                </>
              ) : (
                <>
                  <span className="cosmic-cta-icon"><CosmicIcon name="ui_locked" size={20} decorative /></span>
                  <span>Complete Payment - ${displayPrice}</span>
                </>
              )}
            </button>

            {/* Legal agreement */}
            <p className="cosmic-checkout-legal">
              By completing your purchase you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </form>
        </div>
      )}

      {/* Processing State */}
      {paymentStep === 'processing' && (
        <div className="cosmic-processing-overlay">
          <div className="cosmic-processing-content">
            <div className="cosmic-loading-constellation">
              <div className="loading-star loading-star--1"></div>
              <div className="loading-star loading-star--2"></div>
              <div className="loading-star loading-star--3"></div>
            </div>
            <h3 className="processing-title">Processing Your Payment</h3>
            <p className="processing-message">
              Please wait while we securely process your payment...
            </p>
            <div className="processing-security">
              <span className="security-icon"><CosmicIcon name="ui_locked" size={16} decorative /></span>
              <span>Encrypted with 256-bit SSL</span>
            </div>
          </div>
        </div>
      )}

      {/* Success State */}
      {paymentStep === 'success' && (
        <div className="cosmic-success-overlay">
          <div className="cosmic-success-content">
            <div className="success-icon"><CosmicIcon name="brand_spark" size={32} decorative /></div>
            <h3 className="success-title">Payment Successful!</h3>
            <p className="success-message">
              Welcome to your {getTierDisplayName(targetTier)}! Preparing your cosmic assessment...
            </p>
            <div className="cosmic-loading-constellation">
              <div className="loading-star loading-star--success"></div>
              <div className="loading-star loading-star--success"></div>
              <div className="loading-star loading-star--success"></div>
            </div>
            <div className="success-details">
              <span className="detail-icon"><CosmicIcon name="ui_mail" size={16} decorative /></span>
              <span>Confirmation sent to {billingInfo.email}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CosmicCheckoutModal;
